<template>
    <div class="mic">
        <div v-if="recording" class="recording" @click="stopRecording()" title="点击停止录音">
            <img src="@/theme/images/mic_wave.png" />
            <i class="el-icon-video-pause" />
        </div>
        <div v-else class="no-recording" @click="startRecording()" title="点击开始录音">
            <i class="ecp-iconfont ecp-icon-microphone" />
        </div>
    </div>
</template>

<script>
import HUMAN_AISPEECH_CONFIG from '@/app/constant/HUMAN_AISPEECH_CONFIG.js';
const talkConfig = {
    channel: 1,
    sampleRate: 16000,
    sampleBit: 16,
    sampleByte: 16 / 8,
    asrBuffer: 4096
};

export default {
    name: 'mic',
    data () {
        return {
            tempStr: '',
            tempPrompt: '', // 说话时临时保存之前的prompt
            recording: false,
            mediaStream: '',
            mediaNode: '',
            jsNode: null,
            wsClient: null,
            wsResult: ''
        };
    },
    props: {
        prompt: {
            type: String,
            default: null
        }
    },
    watch: {
        prompt (val) {
            // 文字清空或发送后，断开录音
            if (!val && this.wsClient && this.wsClient.readyState === 1) this.stopRecording();
        }
    },
    methods: {
        webSocketInit () {
            let API_URL = 'wss://asr.dui.ai'; // 直接使用思必驰的服务
            let _serverUrl = `${API_URL}/runtime/v2/recognize?productId=${HUMAN_AISPEECH_CONFIG.productIdServer}&res=comm&language=zh-CN&apikey=${HUMAN_AISPEECH_CONFIG.apikey}`;
            const p = new Promise((resolve, reject) => {
                if ('WebSocket' in window) {
                    this.wsClient = new WebSocket(_serverUrl);
                } else {
                    console.error('Not support websocket');
                    return;
                }

                this.wsClient.onopen = function (event) {
                    console.info('mic: [ws] open');
                    resolve('open');
                };

                let tempStr = ''; // 说话文字缓存
                let tempTimeout = null;
                this.wsClient.onmessage = (event) => {
                    let data = JSON.parse(event.data);
                    let asr = data.result.var || data.result.nlu_rec || data.result.rec;
                    if (asr) {
                        // 实时语音转文字，边说话边显示
                        if (!this.tempPrompt) this.tempPrompt = this.prompt; // 缓存之前的prompt
                        tempStr = asr.replace(/\s+/g, '');
                        this.$emit('update:prompt', this.tempPrompt + tempStr);

                        // 说话结束1秒后，确认输入文字，清理缓存
                        clearTimeout(tempTimeout);
                        tempTimeout = setTimeout(() => {
                            let str = this.tempPrompt + tempStr + '。';
                            this.$emit('update:prompt', str);
                            tempStr = ''; // 清理说话文字缓存
                            this.tempPrompt = ''; // 清理缓存prompt
                        }, 1000);
                    }
                };

                this.wsClient.onclose = () => {
                    this.wsClient = null;
                    if (this.recording) {
                        console.warn('mic: [ws] close,reconnect...');
                        setTimeout(() => {
                            this.webSocketInit();
                        }, 3000);
                    }
                };

                this.wsClient.onerror = function (e) {
                    console.error('mic: [ws] error', e);
                    reject(e);
                };
            });
            return p;
        },

        /**
         * 开始录音
         */
        startRecording () {
            let chunks = [];
            if (!navigator.mediaDevices) {
                this.$message.error('当前环境不支持麦克风设备');
                return;
            }
            this.webSocketInit().then(res => {
                navigator.mediaDevices.getUserMedia({ audio: true })
                    .then((stream) => {
                        this.recording = true;
                        const nav = window.navigator;
                        nav.getUserMedia(
                            {
                                audio: {
                                    channelCount: talkConfig.channel,
                                    sampleRate: talkConfig.sampleRate,
                                    sampleSize: talkConfig.sampleBit,
                                    noiseSuppression: true,
                                    echoCancellation: true
                                }
                            },
                            this.initRecorderSuccess,
                            this.initRecorderonError
                        );
                    })
                    .catch((error) => {
                        this.$message.error('无法获取麦克风设备或权限：' + error);
                        this.wsClient.close();
                    });
            }).catch((error) => {
                this.$message.error('思必驰webSocket接口连接失败' + error);
            });
        },

        initRecorderonError () {
            this.$message.error('获取音频失败');
        },

        initRecorderSuccess (stream) {
            const d = Date;
            let timestamp = d.parse(new Date());
            let json = {
                context: {
                    productId: HUMAN_AISPEECH_CONFIG.productIdServer
                },
                request: {
                    requestId: timestamp,
                    audio: {
                        audioType: 'wav',
                        sampleRate: talkConfig.sampleRate,
                        channel: talkConfig.channel,
                        sampleBytes: talkConfig.sampleByte,
                        compress: 'raw'
                    },
                    asr: {
                        enableFirstDec: true,
                        enableRealTimeFeedback: true,
                        enableVAD: false
                    }
                }
            };
            this.sendText(json);
            this.mediaStream = stream;
            let win = window;
            let audioContext = new (win.AudioContext || win.webkitAudioContext)();
            this.mediaNode = audioContext.createMediaStreamSource(stream);
            this.jsNode = this.createJSNode(audioContext);
            this.jsNode.connect(audioContext.destination);
            this.jsNode.onaudioprocess = (e) => {
                let actualSampleRate = e.inputBuffer.sampleRate;
                let buffer = e.inputBuffer.getChannelData(0);
                let blob = this.getPcmBuffer(buffer, actualSampleRate);
                this.sendData(blob);
            };
            this.mediaNode.connect(this.jsNode);
        },

        sendText (message) {
            this.wsClient.send(JSON.stringify(message));
        },

        createJSNode (audioContext) {
            let creator =
                audioContext.createScriptProcessor || audioContext.createJavaScriptNode;
            creator = creator.bind(audioContext);
            return creator(talkConfig.asrBuffer, talkConfig.channel, talkConfig.channel);
        },

        getPcmBuffer (bytes, actualSampleRate) {
            let interleaved = this.interleave(
                bytes,
                actualSampleRate,
                talkConfig.sampleRate
            );
            let offset = 0;
            const dataLength = interleaved.length * talkConfig.sampleByte;
            const buffer = new ArrayBuffer(dataLength);
            const data = new DataView(buffer);
            for (let i = 0; i < interleaved.length; i += 1, offset += 2) {
                const s = Math.max(-1, Math.min(1, interleaved[i]));
                data.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
            }
            return new Blob([data]);
        },

        interleave (e, sampleRate, outputSampleRate) {
            if (sampleRate === outputSampleRate) return e;
            var t = e.length;
            sampleRate += 0.0;
            outputSampleRate += 0.0;
            var s = 0;
            var o = sampleRate / outputSampleRate;
            var u = Math.ceil((t * outputSampleRate) / sampleRate);
            var a = new Float32Array(u);
            for (let i = 0; i < u; i++) {
                a[i] = e[Math.floor(s)];
                s += o;
            }
            return a;
        },

        sendData (data) {
            if (!this.wsClient || this.wsClient.readyState !== 1) return;
            this.wsClient.send(data);
        },

        /**
         * 停止录音
         */
        stopRecording () {
            this.mediaStream && this.mediaStream.getAudioTracks()[0].stop();
            this.mediaNode && this.mediaNode.disconnect();
            this.jsNode && this.jsNode.disconnect();
            this.recording = false;
            this.wsClient.close();
        }
    },
    beforeDestroy () {
        if (this.wsClient) {
            this.wsClient.close();
        }
    }
};
</script>

<style scoped lang="scss">
.mic {
    height: 32px;
    cursor: pointer;
    display: inline-block;
    // background-color: rgba(255,255,255,0.8);
    .recording, .no-recording {
        display: inline-block;
        vertical-align: middle;
        img {
            height: 28px;
            vertical-align: bottom;
            margin-right: 4px;
        }
        i {
            font-size: 32px;
            opacity: 0.5;
        }
    }
    .no-recording {
        margin-top: 6px;
    }
}
</style>
